import cn from 'classnames';
import { useMemo } from 'react';

import { CssPageBlock } from '@webapp/common/components/survey-custom';
import { CDN_URL } from '@webapp/common/conf';
import { useMobile } from '@webapp/common/hooks/use-mobile';
import { calcHeaderHeight, HeaderTitlePosition, styleStrToObject } from '@webapp/common/lib/ui';

import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { SurveyTitle } from '../survey-title';

import css from './header.css';

export const SurveyHeader: FC<{ className?: string }> = ({ className }) => {
    const isMobile = useMobile();

    const {
        design: {
            brand: {
                headerBackgroundColor,
                headerBackgroundImage,
                headerSize,
                headerStick,
                headerTitlePosition,
                logo,
                logoStyle,
                mobileLogoStyle,
                headerTitleAlign,
            },
        },
    } = useStateSelector(selectInfo);
    const headerHeight = calcHeaderHeight(headerSize);
    const showTitleInHeader = headerTitlePosition === HeaderTitlePosition.IN_HEAD;
    const headerStyles: CSSProperties = useMemo(
        () => ({
            minHeight: `${headerHeight}px`,
            backgroundColor: headerBackgroundColor,
            backgroundImage: headerBackgroundImage ? `url(${CDN_URL + headerBackgroundImage})` : undefined,
        }),
        [headerBackgroundColor, headerBackgroundImage, headerHeight],
    );
    const logoStyles = useMemo(
        () => styleStrToObject(isMobile && mobileLogoStyle ? mobileLogoStyle : logoStyle),
        [logoStyle, mobileLogoStyle, isMobile],
    );

    return (
        <div className={cn(css.headerWrap, className, { [css.sticky]: headerStick })} id='headerWrap'>
            <header className={cn(CssPageBlock.HEADER, css.header)} style={headerStyles}>
                {(logo || showTitleInHeader) && (
                    <SurveyTitle
                        empty={logo && !showTitleInHeader}
                        headerHeight={headerHeight}
                        inBody={false}
                        align={headerTitleAlign}
                    />
                )}
                {logo && (
                    <div
                        className={cn(CssPageBlock.HEADER_LOGO_CONTAINER, css.logoWrap, {
                            [css.logoWrapMobile]: isMobile && mobileLogoStyle,
                        })}
                    >
                        <img
                            alt='logo'
                            className={cn(CssPageBlock.HEADER_LOGO, css.logoImg)}
                            loading='lazy'
                            src={CDN_URL + logo}
                            style={logoStyles}
                        />
                    </div>
                )}
            </header>
        </div>
    );
};
SurveyHeader.displayName = 'SurveyHeader';
